import { Tooltip } from 'bootstrap';
import React, { ChangeEventHandler, MouseEventHandler, useRef, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import InputWithSelectorField from '../../components/InputWithSelectorField';
import { useTranslation } from 'react-i18next';

type DetailProps = {
    key: number;
    propertyName: string;
    defaultValue?: any;
    value?: any;
    checked?: boolean;
    rowColor: string;
    editType: string;
    className?: string;
    pattern?: string;
    submitted?: boolean;
    showInputEle: boolean;
    hideOnNull: boolean;
    handleDoubleClick: MouseEventHandler;
    handleChange: ChangeEventHandler<any>;
    possibilities?: string[] | null;
    possibilityDescriptions?: { [key: string]: string };
    enumMap?: { [key: string]: string } | null;
    description?: string | null;
    conditionToShowReferenceValue?: string | null;
    conditionToShowFieldName?: string | null;
    conditionToShowValue?: string | null;
    onFieldClick?: (fieldName: string) => void;
    isClickable?: boolean;
};

const getWidth = (text: string) => {
    const width = (text.length * 7) + 20;
    return width + 'px';
};

const getInputElement = (
    fixedInputText: string | null,
    defaultValue: any,
    value: any,
    pattern: string | undefined,
    isRequired: boolean,
    handleChange: ChangeEventHandler<any>
) => {
    const isControlled = value !== undefined;

    if (fixedInputText === null) {
        return (
            <span>
                <Form.Control
                    type="text"
                    value={isControlled ? (value ?? '') : undefined}
                    defaultValue={isControlled ? undefined : defaultValue}
                    onChange={(e) => { handleChange(e); }}
                    size="sm"
                    pattern={pattern}
                    required={isRequired}
                    onBlur={(e) => e.target.reportValidity()}
                />
            </span>
        );
    }

    return (
        <>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', display: 'flex' }}>
                <Form.Control
                    onClick={() => {
                        (document.querySelector('#input1') as HTMLElement)?.scrollIntoView();
                        (document.querySelector('#input1') as HTMLElement)?.focus();
                    }}
                    className="fixedInputText d-none d-md-block"
                    size="sm"
                    type="readonly"
                    disabled={false}
                    defaultValue={fixedInputText}
                    style={{
                        backgroundColor: 'white',
                        paddingRight: 0,
                        borderRightColor: 'white',
                        minWidth: getWidth(fixedInputText),
                        maxWidth: getWidth(fixedInputText),
                        zIndex: 1
                    }}
                />
                <Form.Control
                    style={{ marginLeft: -5, paddingLeft: 5, borderLeftColor: 'white', zIndex: 0 }}
                    className="d-none d-md-block"
                    type="text"
                    size="sm"
                    id="input1"
                    value={isControlled ? (value ?? '') : undefined}
                    defaultValue={isControlled ? undefined : defaultValue}
                    onChange={(e) => { handleChange(e); }}
                    pattern={pattern}
                    required={isRequired}
                    onBlur={(e) => e.target.reportValidity()}
                />
            </div>

            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                <Form.Control
                    onClick={() => {
                        (document.querySelector('#input2') as HTMLElement)?.focus();
                    }}
                    className="d-md-none"
                    size="sm"
                    type="readonly"
                    disabled={false}
                    defaultValue={fixedInputText}
                    style={{ backgroundColor: 'white', paddingRight: 0 }}
                />
                <Form.Control
                    className="d-md-none"
                    size="sm"
                    id="input2"
                    value={isControlled ? (value ?? '') : undefined}
                    defaultValue={isControlled ? undefined : defaultValue}
                    onChange={(e) => { handleChange(e); }}
                    pattern={pattern}
                    required={isRequired}
                    onBlur={(e) => e.target.reportValidity()}
                />
            </div>
        </>
    );
};

const DetailItem = ({
    key,
    propertyName,
    defaultValue,
    value,
    checked,
    rowColor,
    className,
    editType,
    pattern,
    submitted,
    showInputEle,
    hideOnNull,
    handleDoubleClick,
    handleChange,
    description,
    possibilities = [],
    possibilityDescriptions = {},
    enumMap = {},
    conditionToShowReferenceValue,
    conditionToShowValue,
    onFieldClick,
    isClickable = false
}: DetailProps) => {
    const tooltipRef = useRef<HTMLSpanElement>(null);

    const isRequired = (pattern: string | undefined) => {
        const ranges = pattern?.split(']{') ?? [];

        if (ranges.length > 0) {
            const range = ranges.pop() ?? '';
            const minValue = Number(range.split(',')?.at(0) ?? 0);
            if (minValue > 0) {
                return true;
            }
        }

        return false;
    };

    const { t } = useTranslation();

    let fixedInputText = null;
    let propertyNameText = t(propertyName);

    if (propertyNameText?.includes('~|~')) {
        const tmp = propertyNameText.split('~|~');
        propertyNameText = tmp[0];
        fixedInputText = tmp[1];
    }

    useEffect(() => {
        if (tooltipRef?.current) {
            const tooltip = new Tooltip(tooltipRef.current, {
                container: 'body',
                trigger: 'click',
                placement: 'right'
            });

            return () => {
                tooltip.dispose();
            };
        }
    }, []);

    const checkboxDefault = defaultValue?.toString?.().toLowerCase() === 'true';
    const isCheckboxControlled = checked !== undefined;
    const isValueControlled = value !== undefined;

    // Conditional visibility based on parent object's field value
    if (conditionToShowReferenceValue && conditionToShowValue) {
        const parentVal = conditionToShowReferenceValue;
        // compare as strings to be permissive with types
        if ((parentVal ?? '') !== conditionToShowValue?.toString()) {
            return <></>;
        }
    }

    const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
        // Prevent interfering with double-click edit if needed
        if (onFieldClick && isClickable) {
            onFieldClick(propertyName);
        }
    };

    return (
        (hideOnNull && (defaultValue === null || defaultValue === undefined) && !isValueControlled) || editType === 'hidden' ? (
            <></>
        ) : (
            <>
                <Row
                    key={key}
                    className={'qvend-table-font ' + (className ?? '')}
                    style={{ background: rowColor, color: 'black' }}
                >
                    <Col
                        xl={6}
                        lg={6}
                        md={6}
                        sm={6}
                        xs={6}
                        className="qvend-table table-column-first q-vend-detail-table-col"
                    >
                        {propertyNameText}
                        {(description !== null && description !== undefined) && (
                            <span data-bs-html="true" title={description} ref={tooltipRef} className="btn-xs">
                                <svg className="flex-shrink-0 bi me-2 icon-24" width="18" height="18" style={{ marginLeft: '5px' }}>
                                    <use href="#info-lamp"></use>
                                </svg>
                            </span>
                        )}
                    </Col>

                    <Col
                        xl={6}
                        lg={6}
                        md={6}
                        sm={6}
                        xs={6}
                        className={`qvend-table q-vend-detail-table-col${isClickable ? ' clickable-field' : ''}`}
                        onDoubleClick={(e) => {
                            handleDoubleClick(e);
                        }}
                        onClick={isClickable ? handleClick : undefined}
                        style={
                        isClickable
                            ? {
                                cursor: 'pointer',
                                color: '#0066cc',
                                textDecoration: 'underline',
                            }
                            : undefined
                        }
                    >
                        {
                            showInputEle ? (
                                editType === 'string' ? (
                                    getInputElement(
                                        fixedInputText,
                                        defaultValue ?? (possibilities?.[0] ?? ""),
                                        value,
                                        pattern,
                                        isRequired(pattern),
                                        handleChange
                                    )
                                ) : editType === 'checkbox' ? (
                                    <span>
                                        <Form.Check
                                            checked={isCheckboxControlled ? checked : undefined}
                                            defaultChecked={isCheckboxControlled ? undefined : checkboxDefault}
                                            onChange={(e) => {
                                                e.target.value = e.target.checked ? 'True' : 'False';
                                                handleChange(e);
                                            }}
                                        />
                                    </span>
                                ) : editType === 'list' && possibilities && possibilities.length > 0 || editType === 'list' && enumMap? (
                                    <Form.Group className="mb-3">
                                    <Form.Select
                                        className="form-control"
                                        style={{ padding: '0.3rem 3rem 0.3rem 1rem' }}
                                        value={isValueControlled ? (value ?? '') : (defaultValue ?? '')}
                                        isInvalid={submitted && !(isValueControlled ? (value ?? '') : (defaultValue ?? ''))}
                                        required={isRequired(pattern)}
                                        onChange={(e) => handleChange(e)}
                                        >
                                        {possibilities ? possibilities
                                            .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                                            .map((v: string) =>
                                                possibilityDescriptions && Object.keys(possibilityDescriptions).length > 0 ? (
                                                    <option key={v} value={v}>
                                                        {possibilityDescriptions[v] ?? ''}
                                                    </option>
                                                ) : (
                                                    <option key={v} value={v}>
                                                        {v}
                                                    </option>
                                                )
                                            ) : enumMap ? Object.entries(enumMap).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })).map(([key, val]) => (
                                                <option key={key} value={val}>
                                                    {val}
                                                </option>
                                            )) : null}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {t('detail.selectType')}
                                    </Form.Control.Feedback>
                                    </Form.Group>
                                ) : editType === 'string-language' && possibilities ? (
                                    <InputWithSelectorField
                                        options={possibilities}
                                        initialTextPerOption={typeof defaultValue === 'string' ? JSON.parse(defaultValue) : defaultValue}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                    />
                                ) : (
                                    <span>{value ?? defaultValue}</span>
                                )
                            ) : editType === 'checkbox' ? (
                                <span>
                                    <Form.Check
                                        checked={isCheckboxControlled ? checked : checkboxDefault}
                                        onChange={(e) => {
                                            e.target.value = e.target.checked ? 'True' : 'False';
                                            handleChange(e);
                                        }}
                                        disabled={true}
                                    />
                                </span>
                            ) : (
                                <span>
                                    {enumMap !== null && enumMap !== undefined && enumMap[(value ?? defaultValue)] !== undefined
                                        ? enumMap[(value ?? defaultValue)]
                                        : editType === 'string-language'
                                            ? JSON.parse(value ?? defaultValue)?.['NL']
                                            : fixedInputText != null
                                                ? fixedInputText + (value ?? defaultValue)
                                                : Array.isArray(value ?? defaultValue)
                                                    ? (value ?? defaultValue).map(([key, value]: [any, any]) => (key + (value !== undefined ? value : '') + ' '))
                                                    : (value ?? defaultValue)}
                                </span>
                            )
                        }
                    </Col>
                </Row>
            </>
        )
    );
};

DetailItem.defaultProps = {
    possibilities: []
};

export default DetailItem;